import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { useTranslation } from '@lt/localization';
import { Searcher as SearcherComponent } from '@lt/components';

import SearchTypeTabs from 'src/components/SearchTypeTabs';
import CigaretteSkeleton from 'src/components/CigaretteSkeleton';
import LazyCigarette from 'src/components/Cigarette/components/LazyCigarette';

import { isSSR } from 'src/helpers/ssr';

import { SEARCH_TYPE_NAMES } from 'src/constants/search';
import { TABS_PAGES } from 'src/components/SearchTypeTabs/constants';

import type { SearcherProps } from './types';

import SearcherStatus from '../../containers/Searcher/SearcherStatus';
import SearcherFormClamped from './SearcherFormClamped';

const Searcher = ({
  isMobile,
  isWhitelabel,
  cigaretteValues,
  searchType,
  setSearchType,
  onSubmitSearch,
  isOpenFiltersList,
  activeFilters,
  ...props
}: SearcherProps) => {
  const [isClamped, setClamped] = useState(isMobile);

  const { t } = useTranslation();

  return (
    <StyledContainer isMobile={isMobile}>
      {!isMobile && <StyledTitle>{t('hotel.cigarette.title')}</StyledTitle>}
      {!isMobile && isSSR ? (
        <StyledCigaretteContainer isMobile={isMobile}>
          <TabsSkeleton />
          <CigaretteSkeleton />
        </StyledCigaretteContainer>
      ) : null}
      {!isSSR ? (
        <StyledCigaretteContainer isMobile={isMobile}>
          <StyledSearchTypeTabs
            selectedTab={searchType}
            setSelectedTab={setSearchType}
            variant={{
              mobile: isMobile,
              wl: isWhitelabel,
              collapsed: false,
              page: TABS_PAGES.HOTEL,
            }}
            isMobile={isMobile}
          />
          {!isClamped ? (
            <CigaretteContainer>
              <LazyCigarette
                cigaretteValues={cigaretteValues}
                isMobile={isMobile}
                searchType={searchType}
                variant="hotel"
                onSubmit={onSubmitSearch}
              />
            </CigaretteContainer>
          ) : (
            <SearcherFormClamped
              cityFrom={cigaretteValues.departure.selectedLabel}
              adults={cigaretteValues.tourists.adults}
              startDate={cigaretteValues.datepicker?.left.date || undefined}
              endDate={cigaretteValues.datepicker?.right.date || undefined}
              kidsCount={cigaretteValues.tourists.kids.length}
              onExpand={() => setClamped(false)}
              isHotelOnly={searchType === SEARCH_TYPE_NAMES.HOTEL}
            />
          )}
        </StyledCigaretteContainer>
      ) : null}
      <SearcherComponent
        isMobile={isMobile}
        /* TODO: типизировать передаваемый компонент
         * @see https://leveltravel.atlassian.net/browse/LT-35957
         */
        // @ts-ignore
        SearcherStatusComponent={SearcherStatus}
        currentSearchTypeTab={searchType}
        searchType={searchType}
        isOpenFiltersList={isOpenFiltersList}
        activeFilters={activeFilters}
        {...props}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    !isMobile &&
    `
    margin-bottom: 10px;
  `}
`;

const StyledCigaretteContainer = styled.div<{ isMobile: boolean }>`
  min-height: 120px;
  padding: 8px;
  border-radius: 6px 6px 0 0;
  box-shadow: rgb(216 214 204 / 90%) 0px 10px 15px 0px,
    rgb(210 208 197 / 90%) 0px 1px 2px 1px;
  ${({ isMobile }) =>
    isMobile &&
    css`
      background-color: ${({ theme }) => theme.colors.beige};
      > form {
        background-color: ${({ theme }) => theme.colors.beige};
        span {
          color: ${({ theme }) => theme.colors.blacklight};
        }
      }
    `}
`;

const StyledTitle = styled.h2`
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: bold;
`;

// чтобы сигарета не скакала я сделал ей фиксированный контейнер
const CigaretteContainer = styled.div`
  width: 100%;
  height: auto;
  min-height: 56px;
  max-width: 1200px;
`;

const TabsSkeleton = styled.div`
  height: 48px;
`;

const StyledSearchTypeTabs = styled(SearchTypeTabs)<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    margin-bottom: 8px;
`}
`;

export default Searcher;
